import { UserX } from "lucide-react";
import queryString from "query-string";
import { Redirect } from "react-router-dom";

import { getCurrentUser, signOut } from "@/auth/firebase";
import { Button } from "@/design-system/components/Button/Button";
import PageWithTitle from "@/PageWithTitle";
import { buildSupportFormURL, pageDetailLines } from "@/support/supportFormURL";

import useAccountStatus from "./useAccountStatus";

type NoAccountPageProps = {
  isSignedIn?: boolean;
  location: { pathname: string; search: string };
};

/**
 * Shown to a signed-in user who has no Freshpaint account. Firebase lets anyone
 * register, but accounts are provisioned only through the onboarding flow or the
 * internal admin page, so we authenticate these users and then grant them
 * nothing beyond this screen.
 */
export function NoAccountPage(props: NoAccountPageProps) {
  if (!props.isSignedIn) {
    const next = props.location.pathname.includes("signout")
      ? null
      : props.location.pathname + props.location.search;
    return <Redirect to={`/login?${queryString.stringify({ next })}`} />;
  }

  return <NoAccountPageHelper />;
}

function NoAccountPageHelper() {
  const currentUser = getCurrentUser();
  const email = currentUser?.email ?? "";

  // Polls in the background: if someone invites this user while they're sitting
  // here, we move them to the invite flow without requiring a manual reload.
  const { data: status } = useAccountStatus();

  if (status?.pending_invite_id) {
    return <Redirect to="/pending-invite" />;
  }

  // An account can also appear without an invite (e.g. an employee adding the
  // user directly), so honor that too rather than stranding them here.
  if (status?.has_account) {
    return <Redirect to="/" />;
  }

  const supportURL = buildSupportFormURL({
    email,
    sections: [
      {
        heading: "Account Details",
        lines: [
          `<strong>User:</strong> ${email || "(unknown)"} (${currentUser?.uid ?? "(unknown)"})`,
          `<strong>Account:</strong> (none — no Freshpaint account found for this user)`,
        ],
      },
      { heading: "Page Details", lines: pageDetailLines() },
    ],
  });

  return (
    <PageWithTitle title="No account found">
      <div className="bg-surface-sunken flex min-h-screen items-center justify-center px-4 py-12">
        <div className="bg-surface-default w-[560px] max-w-full rounded-lg px-8 py-10 text-center shadow-sm">
          <h2 className="text-textDefault text-3xl font-bold">Freshpaint</h2>

          <UserX
            className="text-textDefault mx-auto mt-6"
            size={36}
            strokeWidth={1.5}
            aria-hidden={true}
          />

          <h3 className="text-textDefault mt-6 text-lg font-bold">
            We couldn&rsquo;t find an account using your credentials
          </h3>

          <p className="text-textSubtle mx-auto mt-3 max-w-[440px] text-base">
            Freshpaint is available to customers and approved users only. If you
            believe you should have access, contact support.
          </p>

          {email && (
            <p className="text-textSubtle mt-4 text-sm">
              You&rsquo;re signed in as{" "}
              <span className="text-textDefault font-medium">{email}</span>.
            </p>
          )}

          <Button
            className="mt-6"
            size="lg"
            onClick={() => window.open(supportURL.toString(), "_blank")}
          >
            Contact support
          </Button>

          <p className="mt-6 text-sm">
            <button
              type="button"
              className="text-textSubtle cursor-pointer underline"
              onClick={() => signOut()}
            >
              Sign in with a different account
            </button>
          </p>
        </div>
      </div>
    </PageWithTitle>
  );
}
